import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Plus, Trash2, Upload, X, Layers, Palette, Ruler, Tag } from 'lucide-react';

const SUGGESTIONS = ['Couleur', 'Taille', 'Matiere', 'Style', 'Modele'];

const VariantManager = ({ shopId, product, open, onOpenChange }) => {
  const { api } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newGroupName, setNewGroupName] = useState('');
  const [addingGroup, setAddingGroup] = useState(false);

  const fetchVariants = useCallback(async () => {
    try {
      const res = await api.get(`/shops/${shopId}/products/${product.id}/variants`);
      setGroups(res.data);
    } catch (e) {
      toast.error('Erreur chargement variantes');
    } finally {
      setLoading(false);
    }
  }, [api, shopId, product?.id]);

  useEffect(() => {
    if (open && product) {
      setLoading(true);
      fetchVariants();
    }
  }, [open, product, fetchVariants]);

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    setAddingGroup(true);
    try {
      const res = await api.post(`/shops/${shopId}/products/${product.id}/variants`, {
        name: newGroupName.trim(),
        sortOrder: groups.length
      });
      setGroups([...groups, res.data]);
      setNewGroupName('');
      toast.success('Groupe cree');
    } catch (e) {
      toast.error('Erreur');
    } finally {
      setAddingGroup(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    try {
      await api.delete(`/shops/${shopId}/products/${product.id}/variants/${groupId}`);
      setGroups(groups.filter(g => g.id !== groupId));
      toast.success('Groupe supprime');
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleAddOption = async (groupId, optionName) => {
    if (!optionName.trim()) return;
    try {
      const group = groups.find(g => g.id === groupId);
      const res = await api.post(`/shops/${shopId}/products/${product.id}/variants/${groupId}/options`, {
        name: optionName.trim(),
        sortOrder: group?.options?.length || 0
      });
      setGroups(groups.map(g => {
        if (g.id === groupId) {
          return { ...g, options: [...(g.options || []), res.data] };
        }
        return g;
      }));
      toast.success('Option ajoutee');
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleDeleteOption = async (groupId, optionId) => {
    try {
      await api.delete(`/shops/${shopId}/products/${product.id}/variants/${groupId}/options/${optionId}`);
      setGroups(groups.map(g => {
        if (g.id === groupId) {
          return { ...g, options: (g.options || []).filter(o => o.id !== optionId) };
        }
        return g;
      }));
      toast.success('Option supprimee');
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleUpdateOptionPrice = async (groupId, optionId, priceCents) => {
    try {
      const res = await api.put(`/shops/${shopId}/products/${product.id}/variants/${groupId}/options/${optionId}`, {
        priceCents: priceCents ? parseInt(priceCents) : null
      });
      setGroups(groups.map(g => {
        if (g.id === groupId) {
          return { ...g, options: (g.options || []).map(o => o.id === optionId ? res.data : o) };
        }
        return g;
      }));
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleUploadOptionImage = async (groupId, optionId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post(
        `/shops/${shopId}/products/${product.id}/variants/${groupId}/options/${optionId}/image`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setGroups(groups.map(g => {
        if (g.id === groupId) {
          return { ...g, options: (g.options || []).map(o => o.id === optionId ? { ...o, imageUrl: res.data.imageUrl } : o) };
        }
        return g;
      }));
      toast.success('Image ajoutee');
    } catch (e) {
      toast.error('Erreur upload');
    }
  };

  if (!product) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5" />
            Variantes - {product.name}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : (
          <div className="space-y-6">
            {groups.map(group => (
              <VariantGroupCard
                key={group.id}
                group={group}
                onDeleteGroup={() => handleDeleteGroup(group.id)}
                onAddOption={(name) => handleAddOption(group.id, name)}
                onDeleteOption={(optionId) => handleDeleteOption(group.id, optionId)}
                onUpdatePrice={(optionId, price) => handleUpdateOptionPrice(group.id, optionId, price)}
                onUploadImage={(optionId, file) => handleUploadOptionImage(group.id, optionId, file)}
              />
            ))}

            <div className="border-t pt-4">
              <Label className="text-sm font-medium mb-2 block">Ajouter un groupe de variantes</Label>
              <div className="flex gap-2 flex-wrap mb-2">
                {SUGGESTIONS.filter(s => !groups.some(g => g.name === s)).map(s => (
                  <button
                    key={s}
                    onClick={() => setNewGroupName(s)}
                    className="text-xs px-2.5 py-1 rounded-full border border-dashed border-gray-300 hover:border-primary hover:text-primary transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Ex: Couleur, Taille..."
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateGroup()}
                  data-testid="new-variant-group-input"
                />
                <Button onClick={handleCreateGroup} disabled={addingGroup || !newGroupName.trim()} data-testid="add-variant-group-btn">
                  <Plus className="w-4 h-4 mr-1" /> Ajouter
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const VariantGroupCard = ({ group, onDeleteGroup, onAddOption, onDeleteOption, onUpdatePrice, onUploadImage }) => {
  const [newOptionName, setNewOptionName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const groupIcon = () => {
    const name = group.name.toLowerCase();
    if (name.includes('couleur') || name.includes('color')) return <Palette className="w-4 h-4" />;
    if (name.includes('taille') || name.includes('size')) return <Ruler className="w-4 h-4" />;
    return <Tag className="w-4 h-4" />;
  };

  return (
    <Card data-testid={`variant-group-${group.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {groupIcon()}
            <h3 className="font-semibold">{group.name}</h3>
            <Badge variant="secondary" className="text-xs">{group.options?.length || 0} options</Badge>
          </div>
          <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive h-8 w-8" onClick={() => setConfirmDelete(true)}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>

        <div className="space-y-2">
          {(group.options || []).map(option => (
            <VariantOptionRow
              key={option.id}
              option={option}
              onDelete={() => onDeleteOption(option.id)}
              onUpdatePrice={(price) => onUpdatePrice(option.id, price)}
              onUploadImage={(file) => onUploadImage(option.id, file)}
            />
          ))}
        </div>

        <div className="flex gap-2">
          <Input
            placeholder={`Nouvelle option (ex: ${group.name === 'Couleur' ? 'Rouge, Bleu...' : 'S, M, L...'})`}
            value={newOptionName}
            onChange={(e) => setNewOptionName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newOptionName.trim()) {
                onAddOption(newOptionName);
                setNewOptionName('');
              }
            }}
            className="text-sm"
            data-testid={`add-option-input-${group.id}`}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => { onAddOption(newOptionName); setNewOptionName(''); }}
            disabled={!newOptionName.trim()}
          >
            <Plus className="w-3.5 h-3.5" />
          </Button>
        </div>

        <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer "{group.name}" ?</AlertDialogTitle>
              <AlertDialogDescription>Toutes les options de ce groupe seront supprimees.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={onDeleteGroup} className="bg-destructive hover:bg-destructive/90">Supprimer</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};

const VariantOptionRow = ({ option, onDelete, onUpdatePrice, onUploadImage }) => {
  const [editingPrice, setEditingPrice] = useState(false);
  const [priceValue, setPriceValue] = useState(option.priceCents?.toString() || '');
  const fileInputId = `variant-img-${option.id}`;

  const handlePriceSave = () => {
    onUpdatePrice(priceValue || null);
    setEditingPrice(false);
  };

  return (
    <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50 group" data-testid={`variant-option-${option.id}`}>
      {option.imageUrl ? (
        <div className="w-10 h-10 rounded-md overflow-hidden shrink-0 border relative">
          <img src={`${process.env.REACT_APP_BACKEND_URL}${option.imageUrl}`} alt={option.name} className="w-full h-full object-cover" />
        </div>
      ) : (
        <button
          onClick={() => document.getElementById(fileInputId)?.click()}
          className="w-10 h-10 rounded-md border-2 border-dashed border-gray-300 hover:border-primary flex items-center justify-center shrink-0 transition-colors"
          title="Ajouter une image"
        >
          <Upload className="w-3.5 h-3.5 text-gray-400" />
        </button>
      )}
      <input
        type="file"
        id={fileInputId}
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onUploadImage(file);
          e.target.value = '';
        }}
      />

      <span className="font-medium text-sm flex-1">{option.name}</span>

      {editingPrice ? (
        <div className="flex items-center gap-1">
          <Input
            type="number"
            value={priceValue}
            onChange={(e) => setPriceValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handlePriceSave()}
            onBlur={handlePriceSave}
            className="w-24 h-7 text-xs"
            placeholder="Centimes"
            autoFocus
          />
        </div>
      ) : (
        <button
          onClick={() => setEditingPrice(true)}
          className="text-xs text-muted-foreground hover:text-primary transition-colors px-2 py-1 rounded"
          title="Modifier le prix"
        >
          {option.priceCents ? `${(option.priceCents / 100).toFixed(2)} \u20ac` : 'Prix de base'}
        </button>
      )}

      {option.imageUrl && (
        <button
          onClick={() => document.getElementById(fileInputId)?.click()}
          className="opacity-0 group-hover:opacity-100 transition-opacity"
          title="Changer l'image"
        >
          <Upload className="w-3.5 h-3.5 text-muted-foreground" />
        </button>
      )}

      <button
        onClick={onDelete}
        className="opacity-0 group-hover:opacity-100 transition-opacity text-destructive"
        title="Supprimer"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default VariantManager;
